import { FormEvent, useEffect, useMemo, useRef, useState } from "react";

import { api } from "../../api/client";
import { AlertBanner } from "../feedback/AlertBanner";
import { EarTag, useFormatEarTag } from "../tags/EarTag";
import { AnimalDetailModal } from "../modals/AnimalDetailModal";
import { Field } from "../forms/Field";
import { FormModal } from "../modals/FormModal";
import { apiError } from "../../lib/apiError";
import { mutateOrQueue } from "../../lib/offline/helpers";
import { appendToReplicaList } from "../../lib/offline/replica";
import {
  formatDate,
  formatMoney,
  normalizeTag,
  PROGRAM_STATUS_LABELS,
  SEX_LABELS,
  ANIMAL_STATUS_LABELS,
  todayIso,
} from "../../lib/format";
import { passwordFieldProps } from "../../lib/inputPrivacy";
import { DEFAULT_SPECIES, getSpecies } from "../../lib/species/species";
import {
  AUTO_PLACEHOLDER,
  MANUAL_PREFIX,
  formatTagForMode,
  protectManualPrefixValue,
  tagMatchesFilter,
  typedTagFilterQuery,
  type TagInputMode,
} from "../../lib/tagVerificationInput";
import type {
  AnimalLookup,
  AnimalLookupBreedingGroup,
  AnimalLookupBirthSeason,
  FinanceCategory,
  FinanceEntryType,
  FinanceTransaction,
  Sex,
  Animal,
} from "../../types";

type Phase = "search" | "result" | "not_found" | "ask_next";
const species = getSpecies(DEFAULT_SPECIES);
type Action =
  | null
  | "mating"
  | "lambing"
  | "finance"
  | "todo"
  | "stats";

type OffspringDraft = {
  sex: Sex;
  working_tag: string;
  official_tag: string;
  father_tag: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onTodosChanged?: () => void;
};

function emptyLamb(fatherTag = ""): OffspringDraft {
  return { sex: "female", working_tag: "", official_tag: "", father_tag: fatherTag };
}

function passwordOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed || null;
}

async function fetchAnimalPdf(tag: string): Promise<Blob> {
  const response = await fetch(`/api/reports/animals/${encodeURIComponent(tag)}.pdf`, {
    credentials: "same-origin",
    headers: { Accept: "application/pdf" },
  });
  if (!response.ok) {
    throw new Error("Nie udało się pobrać raportu PDF.");
  }
  return response.blob();
}

export function AnimalQuickLookup({ open, onClose, onTodosChanged }: Props) {
  const formatTag = useFormatEarTag();
  const [phase, setPhase] = useState<Phase>("search");
  const [mode, setMode] = useState<TagInputMode>("manual");
  const [tagInput, setTagInput] = useState(MANUAL_PREFIX);
  const [queriedTag, setQueriedTag] = useState("");
  const [lookup, setLookup] = useState<AnimalLookup | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState<Action>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTag, setDetailTag] = useState<string | null>(null);

  const [matingGroupId, setMatingGroupId] = useState<number | null>(null);
  const [matingDate, setMatingDate] = useState(todayIso());
  const [matingRam, setMatingRam] = useState("");
  const [matingUncertain, setMatingUncertain] = useState(false);
  const [matingPassword, setMatingPassword] = useState("");

  const [lambingSeasonId, setBirthSeasonId] = useState<number | null>(null);
  const [lambingDate, setLambingDate] = useState(todayIso());
  const [lambs, setLambs] = useState<OffspringDraft[]>([emptyLamb()]);
  const [lambingPassword, setLambingPassword] = useState("");

  const [financeType, setFinanceType] = useState<FinanceEntryType>("expense");
  const [financeAmount, setFinanceAmount] = useState("");
  const [financeDate, setFinanceDate] = useState(todayIso());
  const [financeCategory, setFinanceCategory] = useState("");
  const [financeNote, setFinanceNote] = useState("");
  const [categories, setCategories] = useState<FinanceCategory[]>([]);

  const [todoTitle, setTodoTitle] = useState("");
  const [todoDescription, setTodoDescription] = useState("");
  const [todoDueDate, setTodoDueDate] = useState("");
  const [knownTags, setKnownTags] = useState<string[]>([]);

  function resetSearch(keepMode = true) {
    setPhase("search");
    setLookup(null);
    setQueriedTag("");
    setError(null);
    setInfo(null);
    setAction(null);
    setDetailOpen(false);
    setDetailTag(null);
    if (keepMode) {
      setTagInput(mode === "manual" ? MANUAL_PREFIX : "");
    } else {
      setMode("manual");
      setTagInput(MANUAL_PREFIX);
    }
  }

  function handleClose() {
    resetSearch(false);
    onClose();
  }

  function cancelAction() {
    setAction(null);
    setError(null);
  }

  function closeDetail() {
    setDetailOpen(false);
    setDetailTag(null);
  }

  const actionRef = useRef<Action>(null);
  const detailOpenRef = useRef(false);
  actionRef.current = action;
  detailOpenRef.current = detailOpen;

  /** Escape / klik w tło / wstecz: najpierw zamknij akcję lub szczegóły, potem cały lookup. */
  function onShellClose() {
    if (actionRef.current) {
      cancelAction();
      return;
    }
    if (detailOpenRef.current) {
      closeDetail();
      return;
    }
    handleClose();
  }

  useEffect(() => {
    if (!open) {
      return;
    }
    resetSearch(false);
    void (async () => {
      const result = await api.GET("/api/tags");
      if (result.error || !result.data || typeof result.data !== "object") {
        return;
      }
      const payload = result.data as { tags?: string[] };
      setKnownTags(payload.tags ?? []);
    })();
  }, [open]);

  useEffect(() => {
    if (action !== "finance") {
      return;
    }
    void (async () => {
      const result = await api.GET("/api/finance/categories");
      if (result.error) {
        return;
      }
      const list = (result.data as FinanceCategory[]) ?? [];
      setCategories(list);
      setFinanceCategory((current) => {
        if (current && list.some((item) => item.id === current && item.type === financeType)) {
          return current;
        }
        return list.find((item) => item.type === financeType)?.id ?? "";
      });
    })();
  }, [action, financeType]);

  const animal = lookup?.animal ?? null;
  const animalsForDetail: Animal[] = animal ? [animal] : [];
  const matingGroup =
    lookup?.breeding_groups.find((group) => group.id === matingGroupId) ??
    lookup?.breeding_groups[0] ??
    null;
  const lambingSeason =
    lookup?.birth_seasons.find((season) => season.id === lambingSeasonId) ??
    lookup?.birth_seasons[0] ??
    null;
  const financeCategories = categories.filter((item) => item.type === financeType);
  const lookupQuery = typedTagFilterQuery(mode, tagInput);
  const tagSuggestions = useMemo(() => {
    if (!lookupQuery) {
      return [];
    }
    return knownTags.filter((tag) => tagMatchesFilter(tag, lookupQuery)).slice(0, 8);
  }, [knownTags, lookupQuery]);

  async function lookupTag(raw: string) {
    const normalized = normalizeTag(raw);
    if (!normalized) {
      setError("Podaj numer kolczyka.");
      return;
    }
    setBusy(true);
    setError(null);
    setInfo(null);
    setQueriedTag(normalized);
    const result = await api.GET("/api/animals/{tag}/lookup", {
      params: { path: { tag: normalized } },
    });
    setBusy(false);
    if (result.error) {
      if (result.response?.status === 404) {
        setLookup(null);
        setPhase("not_found");
        setError(null);
        return;
      }
      setError(apiError(result.error, "Nie udało się wyszukać owcy."));
      return;
    }
    const data = result.data as AnimalLookup;
    setLookup(data);
    setPhase("result");
    setMatingGroupId(data.breeding_groups[0]?.id ?? null);
    setBirthSeasonId(data.birth_seasons[0]?.id ?? null);
  }

  async function onSearch(event: FormEvent) {
    event.preventDefault();
    const formatted = formatTagForMode(mode, tagInput);
    const query = typedTagFilterQuery(mode, tagInput);
    if (!query && !normalizeTag(formatted)) {
      setError("Podaj numer kolczyka.");
      return;
    }
    const localMatches = query
      ? knownTags.filter((tag) => tagMatchesFilter(tag, query))
      : [];
    if (localMatches.length === 1) {
      await lookupTag(localMatches[0]);
      return;
    }
    if (localMatches.length > 1) {
      const exact = localMatches.find(
        (tag) => normalizeTag(tag) === normalizeTag(formatted),
      );
      if (exact) {
        await lookupTag(exact);
        return;
      }
      setError("Znaleziono kilka zwierząt — wybierz kolczyk z listy albo doprecyzuj numer.");
      return;
    }
    await lookupTag(formatted);
  }

  function afterSuccessfulSave(message: string) {
    setInfo(message);
    setAction(null);
    setPhase("ask_next");
  }

  function openMating() {
    if (!lookup || lookup.breeding_groups.length === 0) {
      setError("Ta owca nie należy do żadnej aktywnej grupy stanówki.");
      return;
    }
    const group = lookup.breeding_groups[0];
    setMatingGroupId(group.id);
    setMatingDate(group.mating?.mated_at || todayIso());
    setMatingUncertain(Boolean(group.mating?.sire_uncertain || (group.mating && !group.mating.sire_tag)));
    setMatingRam(group.mating?.sire_tag || "");
    setMatingPassword("");
    setError(null);
    setAction("mating");
  }

  function selectMatingGroup(group: AnimalLookupBreedingGroup) {
    setMatingGroupId(group.id);
    setMatingDate(group.mating?.mated_at || todayIso());
    setMatingUncertain(Boolean(group.mating?.sire_uncertain || (group.mating && !group.mating.sire_tag)));
    setMatingRam(group.mating?.sire_tag || "");
  }

  async function onSaveMating(event: FormEvent) {
    event.preventDefault();
    if (!animal || !matingGroup) {
      return;
    }
    if (!matingUncertain && !matingRam) {
      setError("Wybierz tryka albo ustaw tryb „Niepewny tryk”.");
      return;
    }
    setBusy(true);
    const result = await mutateOrQueue(
      "PUT",
      `/api/breeding-seasons/${matingGroup.id}/matings/${animal.tag}`,
      {
        mated_at: matingDate,
        sire_tag: matingUncertain ? null : matingRam || null,
        sire_uncertain: matingUncertain,
        password: passwordOrNull(matingPassword),
      },
    );
    setBusy(false);
    if (!result.success) {
      setError(apiError(result.error, "Nie udało się zapisać pokrycia."));
      return;
    }
    afterSuccessfulSave(result.online ? "Zapisano pokrycie." : "Zapisano pokrycie (offline - zostanie zsynchronizowany).");
  }

  function openLambing() {
    if (!lookup || lookup.birth_seasons.length === 0) {
      setError("Ta owca nie należy do żadnego aktywnego sezonu wykotów.");
      return;
    }
    const season = lookup.birth_seasons[0];
    setBirthSeasonId(season.id);
    setLambingDate(todayIso());
    const suggested =
      lookup.breeding_groups.find((g) => g.birth_season_id === season.id)?.mating?.sire_tag || "";
    setLambs([emptyLamb(suggested || "")]);
    setLambingPassword("");
    setError(null);
    setAction("lambing");
  }

  function selectBirthSeason(season: AnimalLookupBirthSeason) {
    setBirthSeasonId(season.id);
    const suggested =
      lookup?.breeding_groups.find((g) => g.birth_season_id === season.id)?.mating?.sire_tag || "";
    setLambs([emptyLamb(suggested || "")]);
  }

  async function onSaveLambing(event: FormEvent) {
    event.preventDefault();
    if (!animal || !lambingSeason) {
      return;
    }
    if (lambingSeason.has_event) {
      setError("W tym sezonie wykot dla tej owcy jest już odnotowany.");
      return;
    }
    const payloadLambs = lambs
      .map((lamb) => ({
        sex: lamb.sex,
        working_tag: lamb.working_tag.trim(),
        official_tag: normalizeTag(lamb.official_tag) || null,
        father_tag: lamb.father_tag.trim() || null,
      }))
      .filter((lamb) => lamb.working_tag);
    if (payloadLambs.length === 0) {
      setError("Dodaj co najmniej jedno jagnię z numerem roboczym.");
      return;
    }
    const freeSet = new Set(
      (lambingSeason.free_official_tags ?? []).map((tag) => normalizeTag(tag)).filter(Boolean),
    );
    for (const lamb of payloadLambs) {
      if (lamb.official_tag && !freeSet.has(lamb.official_tag)) {
        setError(
          `Numer oficjalny „${formatTag(lamb.official_tag)}” nie istnieje w puli Wolne kolczyki.`,
        );
        return;
      }
    }
    setBusy(true);
    const clientId = crypto.randomUUID();
    const result = await mutateOrQueue(
      "POST",
      `/api/birth-seasons/${lambingSeason.id}/events`,
      {
        password: passwordOrNull(lambingPassword),
        mother_tag: animal.tag,
        birth_date: lambingDate,
        offspring: payloadLambs,
        client_id: clientId,
      },
      clientId,
    );
    setBusy(false);
    if (!result.success) {
      setError(apiError(result.error, "Nie udało się odnotować wykotu."));
      return;
    }
    afterSuccessfulSave(result.online ? "Zapisano wykot." : "Zapisano wykot (offline - zostanie zsynchronizowany).");
  }

  function openFinance(type: FinanceEntryType) {
    setFinanceType(type);
    setFinanceAmount("");
    setFinanceDate(todayIso());
    setFinanceNote("");
    const first = categories.find((item) => item.type === type);
    setFinanceCategory(first?.id ?? "");
    setError(null);
    setAction("finance");
  }

  async function onSaveFinance(event: FormEvent) {
    event.preventDefault();
    if (!animal) {
      return;
    }
    const amount = Number(financeAmount.replace(",", "."));
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Podaj kwotę większą od zera.");
      return;
    }
    if (!financeCategory) {
      setError("Wybierz kategorię.");
      return;
    }
    setBusy(true);
    const clientId = crypto.randomUUID();
    const result = await mutateOrQueue(
      "POST",
      "/api/finance/transactions",
      {
        type: financeType,
        amount,
        date: financeDate,
        category: financeCategory,
        note: financeNote.trim() || null,
        assign_tags: [animal.tag],
        assign_herd: false,
        has_photo: false,
        client_id: clientId,
      },
      clientId,
    );
    setBusy(false);
    if (!result.success) {
      setError(apiError(result.error, "Nie udało się dodać transakcji."));
      return;
    }
    if (!result.online) {
      await appendToReplicaList<FinanceTransaction>("finance:transactions", {
        id: -Date.now(),
        type: financeType,
        amount,
        date: financeDate,
        category: financeCategory,
        subcategory: null,
        note: financeNote.trim() || null,
        assign: null,
        assign_herd: false,
        assign_tags: [animal.tag],
        assign_count: 1,
        amount_per_unit: amount,
        has_photo: false,
        created_at: new Date().toISOString(),
      });
    }
    const msg = financeType === "expense" ? "Dodano wydatek." : "Dodano przychód.";
    afterSuccessfulSave(result.online ? msg : `${msg} (offline - zostanie zsynchronizowany)`);
  }

  function openTodo(prefill?: string) {
    setTodoTitle(prefill || (animal ? `Zadanie dla ${animal.tag}` : ""));
    setTodoDescription("");
    setTodoDueDate("");
    setError(null);
    setAction("todo");
  }

  async function onSaveTodo(event: FormEvent) {
    event.preventDefault();
    const trimmed = todoTitle.trim();
    if (!trimmed) {
      setError("Podaj tytuł zadania.");
      return;
    }
    setBusy(true);
    const clientId = crypto.randomUUID();
    const result = await mutateOrQueue(
      "POST",
      "/api/todos",
      {
        title: trimmed,
        description: todoDescription.trim() || null,
        due_date: todoDueDate || null,
        client_id: clientId,
      },
      clientId,
    );
    setBusy(false);
    if (!result.success) {
      setError(apiError(result.error, "Nie udało się dodać zadania."));
      return;
    }
    if (result.online) {
      onTodosChanged?.();
    }
    afterSuccessfulSave(result.online ? "Dodano zadanie." : "Dodano zadanie (offline - zostanie zsynchronizowane).");
  }

  async function openPdf() {
    if (!animal) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const blob = await fetchAnimalPdf(animal.tag);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Nie udało się otworzyć PDF.");
    } finally {
      setBusy(false);
    }
  }

  const title =
    phase === "search"
      ? species.labels.lookup_title
      : phase === "not_found"
        ? "Nie znaleziono"
        : phase === "ask_next"
          ? "Gotowe"
          : animal
            ? animal.tag
            : species.labels.lookup_heading;

  const overlayOpen = Boolean(action) || detailOpen;

  return (
    <>
      <FormModal
        open={open}
        obscured={overlayOpen}
        onClose={onShellClose}
        title={title}
        size={phase === "result" ? "lg" : "md"}
        testId="sheep-quick-lookup"
      >
        <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
        {info && phase !== "ask_next" ? (
          <AlertBanner variant="ok" message={info} onDismiss={() => setInfo(null)} />
        ) : null}

        {phase === "search" ? (
          <form
            className="sheep-lookup__search"
            onSubmit={onSearch}
            autoComplete="off"
          >
            <p className="muted">
              Wprowadź numer kolczyka ręcznie albo zeskanuj czytnikiem (tryb automatyczny).
            </p>
            <div className="form__row">
              <Field label="Tryb wprowadzania">
                <select
                  className="input"
                  data-testid="sheep-lookup-mode"
                  value={mode}
                  onChange={(event) => {
                    const next = event.target.value as TagInputMode;
                    setMode(next);
                    setTagInput(next === "manual" ? MANUAL_PREFIX : "");
                  }}
                >
                  <option value="manual">Ręcznie</option>
                  <option value="auto">Automatycznie</option>
                </select>
              </Field>
              <Field label="Numer kolczyka">
                <input
                  className="input"
                  data-testid="sheep-lookup-input"
                  value={tagInput}
                  placeholder={mode === "auto" ? AUTO_PLACEHOLDER : `${MANUAL_PREFIX}…`}
                  autoComplete="off"
                  autoFocus
                  onChange={(event) => {
                    const value =
                      mode === "manual"
                        ? protectManualPrefixValue(event.target.value)
                        : event.target.value;
                    setTagInput(value);
                  }}
                  onSelect={(event) => {
                    if (mode !== "manual") {
                      return;
                    }
                    const input = event.currentTarget;
                    if ((input.selectionStart ?? 0) < MANUAL_PREFIX.length) {
                      input.setSelectionRange(MANUAL_PREFIX.length, MANUAL_PREFIX.length);
                    }
                  }}
                />
              </Field>
            </div>
            {lookupQuery ? (
              <div className="sheep-lookup__suggestions" data-testid="sheep-lookup-suggestions">
                {tagSuggestions.length === 0 ? (
                  <p className="muted sheep-lookup__suggestions-empty">
                    Brak kolczyków pasujących do wpisanego numeru.
                  </p>
                ) : (
                  <ul className="sheep-lookup__suggestion-list">
                    {tagSuggestions.map((tag) => (
                      <li key={tag}>
                        <button
                          type="button"
                          className="sheep-lookup__suggestion"
                          disabled={busy}
                          onClick={() => void lookupTag(tag)}
                        >
                          <EarTag value={tag} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={handleClose} disabled={busy}>
                Anuluj
              </button>
              <button className="btn" type="submit" disabled={busy} data-testid="sheep-lookup-submit">
                Szukaj
              </button>
            </div>
          </form>
        ) : null}

        {phase === "not_found" ? (
          <div className="sheep-lookup__not-found">
            <p>
              Nie znaleziono zwierzęcia o numerze <strong><EarTag value={queriedTag} /></strong>.
            </p>
            <p className="muted">Możesz dodać zadanie, żeby później zweryfikować ten kolczyk.</p>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => resetSearch()}>
                Szukaj ponownie
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => openTodo(`Zweryfikować kolczyk ${queriedTag}`)}
              >
                Dodaj zadanie
              </button>
            </div>
          </div>
        ) : null}

        {phase === "result" && animal && lookup ? (
          <div className="sheep-lookup__result">
            <dl className="sheep-lookup__facts">
              <div>
                <dt>Kolczyk</dt>
                <dd>
                  <EarTag value={animal.tag} />
                </dd>
              </div>
              <div>
                <dt>Płeć</dt>
                <dd>{SEX_LABELS[animal.sex] || animal.sex}</dd>
              </div>
              <div>
                <dt>Rasa</dt>
                <dd>{animal.breed || "—"}</dd>
              </div>
              <div>
                <dt>Program</dt>
                <dd>
                  {PROGRAM_STATUS_LABELS[animal.program_status] || animal.program_status}
                </dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{ANIMAL_STATUS_LABELS[animal.status] || animal.status}</dd>
              </div>
              <div>
                <dt>Urodzenie</dt>
                <dd>{formatDate(animal.birth_date)}</dd>
              </div>
            </dl>

            <h4 className="sheep-lookup__actions-title">Akcje</h4>
            <div className="sheep-lookup__actions">
              <button
                type="button"
                className="chip"
                onClick={() => {
                  setDetailTag(animal.tag);
                  setDetailOpen(true);
                }}
              >
                Informacje
              </button>
              <button type="button" className="chip" onClick={openMating}>
                Pokrycie
              </button>
              <button type="button" className="chip" onClick={openLambing}>
                Zgłoś wykot
              </button>
              <button type="button" className="chip" onClick={() => openFinance("expense")}>
                Wydatek
              </button>
              <button type="button" className="chip" onClick={() => openFinance("income")}>
                Przychód
              </button>
              <button type="button" className="chip" onClick={() => openTodo()}>
                Zadanie
              </button>
              <button type="button" className="chip" onClick={() => setAction("stats")}>
                Statystyki
              </button>
              <button type="button" className="chip" onClick={() => void openPdf()} disabled={busy}>
                Raport PDF
              </button>
            </div>

            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => resetSearch()}>
                Inna sztuka
              </button>
              <button type="button" className="btn btn--ghost" onClick={handleClose}>
                Zamknij
              </button>
            </div>
          </div>
        ) : null}

        {phase === "ask_next" ? (
          <div className="sheep-lookup__ask-next">
            {info ? <AlertBanner variant="ok" message={info} onDismiss={() => setInfo(null)} /> : null}
            <p>Wyszukać kolejną sztukę?</p>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={handleClose}>
                Nie
              </button>
              <button type="button" className="btn" onClick={() => resetSearch()} autoFocus>
                Tak
              </button>
            </div>
          </div>
        ) : null}
      </FormModal>

      <AnimalDetailModal
        tag={open && detailOpen ? detailTag : null}
        animals={animalsForDetail}
        onClose={closeDetail}
        onOpenTag={(tag) => setDetailTag(tag)}
        skipHistory
      />

      <FormModal
        open={open && action === "mating"}
        onClose={cancelAction}
        skipHistory
        title="Pokrycie"
        testId="sheep-lookup-mating"
        onSubmit={onSaveMating}
      >
        <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
        {lookup && lookup.breeding_groups.length > 1 ? (
          <Field label="Grupa stanówki">
            <select
              className="input"
              value={matingGroup?.id ?? ""}
              onChange={(event) => {
                const group = lookup.breeding_groups.find(
                  (item) => item.id === Number(event.target.value),
                );
                if (group) {
                  selectMatingGroup(group);
                }
              }}
            >
              {lookup.breeding_groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </Field>
        ) : matingGroup ? (
          <p className="muted">Grupa: {matingGroup.name}</p>
        ) : null}
        <Field label="Data pokrycia">
          <input
            className="input"
            type="date"
            value={matingDate}
            onChange={(event) => setMatingDate(event.target.value)}
            required
          />
        </Field>
        <label className="list__check">
          <input
            type="checkbox"
            checked={matingUncertain}
            onChange={(event) => setMatingUncertain(event.target.checked)}
          />
          <span>Niepewny tryk</span>
        </label>
        {!matingUncertain ? (
          <Field label="Tryk">
            <select
              className="input"
              value={matingRam}
              onChange={(event) => setMatingRam(event.target.value)}
              required
            >
              <option value="">Wybierz…</option>
              {(matingGroup?.sires ?? []).map((ram) => (
                <option key={ram.tag} value={ram.tag}>
                  {formatTag(ram.tag)}
                  {ram.sire_color ? ` (${ram.sire_color})` : ""}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <Field label="Hasło administratora (jeśli wymagane)">
          <input
            className="input"
            type="password"
            value={matingPassword}
            onChange={(event) => setMatingPassword(event.target.value)}
            {...passwordFieldProps("sheep-lookup-mating-password")}
          />
        </Field>
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={cancelAction} disabled={busy}>
            Anuluj
          </button>
          <button className="btn" type="submit" disabled={busy}>
            Zapisz
          </button>
        </div>
      </FormModal>

      <FormModal
        open={open && action === "lambing"}
        onClose={cancelAction}
        skipHistory
        title="Zgłoszenie wykotu"
        size="xl"
        testId="sheep-lookup-lambing"
        onSubmit={onSaveLambing}
      >
        <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
        {lookup && lookup.birth_seasons.length > 1 ? (
          <Field label="Sezon wykotów">
            <select
              className="input"
              value={lambingSeason?.id ?? ""}
              onChange={(event) => {
                const season = lookup.birth_seasons.find(
                  (item) => item.id === Number(event.target.value),
                );
                if (season) {
                  selectBirthSeason(season);
                }
              }}
            >
              {lookup.birth_seasons.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.name}
                  {season.has_event ? " (już zgłoszony)" : ""}
                </option>
              ))}
            </select>
          </Field>
        ) : lambingSeason ? (
          <p className="muted">
            Sezon: {lambingSeason.name}
            {lambingSeason.has_event ? " — wykot już odnotowany" : ""}
          </p>
        ) : null}
        <p className="muted">
          Matka: <EarTag value={animal?.tag} />
        </p>
        <Field label="Data urodzenia">
          <input
            className="input"
            type="date"
            value={lambingDate}
            onChange={(event) => setLambingDate(event.target.value)}
            required
            disabled={Boolean(lambingSeason?.has_event)}
          />
        </Field>
        <div className="sheep-lookup__lambs">
          {lambs.map((lamb, index) => (
            <div key={index} className="form__row sheep-lookup__lamb-row">
              <Field label={`Jagnię ${index + 1} — płeć`}>
                <select
                  className="input"
                  value={lamb.sex}
                  onChange={(event) => {
                    const next = lambs.slice();
                    next[index] = { ...lamb, sex: event.target.value as Sex };
                    setLambs(next);
                  }}
                  disabled={Boolean(lambingSeason?.has_event)}
                >
                  <option value="female">Samica</option>
                  <option value="male">Samiec</option>
                </select>
              </Field>
              <Field label="Nr roboczy">
                <input
                  className="input"
                  value={lamb.working_tag}
                  onChange={(event) => {
                    const next = lambs.slice();
                    next[index] = { ...lamb, working_tag: event.target.value };
                    setLambs(next);
                  }}
                  required
                  disabled={Boolean(lambingSeason?.has_event)}
                />
              </Field>
              <Field label="Kolczyk oficjalny">
                <input
                  className="input"
                  value={lamb.official_tag}
                  onChange={(event) => {
                    const next = lambs.slice();
                    next[index] = { ...lamb, official_tag: event.target.value };
                    setLambs(next);
                  }}
                  disabled={Boolean(lambingSeason?.has_event)}
                />
              </Field>
              <Field label="Ojciec">
                <input
                  className="input"
                  value={lamb.father_tag}
                  onChange={(event) => {
                    const next = lambs.slice();
                    next[index] = { ...lamb, father_tag: event.target.value };
                    setLambs(next);
                  }}
                  disabled={Boolean(lambingSeason?.has_event)}
                />
              </Field>
            </div>
          ))}
        </div>
        {!lambingSeason?.has_event ? (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setLambs((current) => [...current, emptyLamb(current[0]?.father_tag || "")])}
          >
            Dodaj jagnię
          </button>
        ) : null}
        <Field label="Hasło administratora (jeśli wymagane)">
          <input
            className="input"
            type="password"
            value={lambingPassword}
            onChange={(event) => setLambingPassword(event.target.value)}
            {...passwordFieldProps("sheep-lookup-lambing-password")}
            disabled={Boolean(lambingSeason?.has_event)}
          />
        </Field>
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={cancelAction} disabled={busy}>
            Anuluj
          </button>
          <button
            className="btn"
            type="submit"
            disabled={busy || Boolean(lambingSeason?.has_event)}
          >
            Zapisz wykot
          </button>
        </div>
      </FormModal>

      <FormModal
        open={open && action === "finance"}
        onClose={cancelAction}
        skipHistory
        title={financeType === "expense" ? "Dodaj wydatek" : "Dodaj przychód"}
        testId="sheep-lookup-finance"
        onSubmit={onSaveFinance}
      >
        <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
        <p className="muted">
          Przypisanie: <EarTag value={animal?.tag} />
        </p>
        <div className="form__row">
          <Field label="Typ">
            <select
              className="input"
              value={financeType}
              onChange={(event) => {
                const nextType = event.target.value as FinanceEntryType;
                setFinanceType(nextType);
                const first = categories.find((item) => item.type === nextType);
                setFinanceCategory(first?.id ?? "");
              }}
            >
              <option value="expense">Wydatek</option>
              <option value="income">Przychód</option>
            </select>
          </Field>
          <Field label="Kwota (zł)">
            <input
              className="input"
              inputMode="decimal"
              value={financeAmount}
              onChange={(event) => setFinanceAmount(event.target.value)}
              required
            />
          </Field>
          <Field label="Data">
            <input
              className="input"
              type="date"
              value={financeDate}
              onChange={(event) => setFinanceDate(event.target.value)}
              required
            />
          </Field>
        </div>
        <Field label="Kategoria">
          <select
            className="input"
            value={financeCategory}
            onChange={(event) => setFinanceCategory(event.target.value)}
            required
          >
            <option value="">Wybierz…</option>
            {financeCategories.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Notatka (opcjonalnie)">
          <input
            className="input"
            value={financeNote}
            onChange={(event) => setFinanceNote(event.target.value)}
            maxLength={200}
          />
        </Field>
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={cancelAction} disabled={busy}>
            Anuluj
          </button>
          <button className="btn" type="submit" disabled={busy}>
            Zapisz
          </button>
        </div>
      </FormModal>

      <FormModal
        open={open && action === "todo"}
        onClose={cancelAction}
        skipHistory
        title="Nowe zadanie"
        testId="sheep-lookup-todo"
        onSubmit={onSaveTodo}
      >
        <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
        <Field label="Tytuł">
          <input
            className="input"
            value={todoTitle}
            onChange={(event) => setTodoTitle(event.target.value)}
            maxLength={200}
            required
            autoFocus
          />
        </Field>
        <Field label="Opis (opcjonalnie)">
          <textarea
            className="input"
            value={todoDescription}
            onChange={(event) => setTodoDescription(event.target.value)}
            maxLength={500}
            rows={3}
          />
        </Field>
        <Field label="Termin (opcjonalnie)">
          <input
            className="input"
            type="date"
            value={todoDueDate}
            onChange={(event) => setTodoDueDate(event.target.value)}
          />
        </Field>
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={cancelAction} disabled={busy}>
            Anuluj
          </button>
          <button className="btn" type="submit" disabled={busy}>
            Dodaj
          </button>
        </div>
      </FormModal>

      <FormModal
        open={open && action === "stats"}
        onClose={cancelAction}
        skipHistory
        title={`Statystyki · ${formatTag(animal?.tag)}`}
        testId="sheep-lookup-stats"
      >
        {lookup ? (
          <dl className="sheep-lookup__facts">
            <div>
              <dt>Wiek</dt>
              <dd>
                {lookup.stats.age_years != null
                  ? `${lookup.stats.age_years.toLocaleString("pl-PL", {
                      maximumFractionDigits: 1,
                    })} lat`
                  : "—"}
              </dd>
            </div>
            <div>
              <dt>Potomstwo w stadzie</dt>
              <dd>{lookup.stats.children_count}</dd>
            </div>
            <div>
              <dt>Pokrycia</dt>
              <dd>{lookup.stats.matings_count}</dd>
            </div>
            <div>
              <dt>Wykoty</dt>
              <dd>{lookup.stats.birth_events_count}</dd>
            </div>
            <div>
              <dt>Jagnięta</dt>
              <dd>{lookup.stats.offspring_born_count}</dd>
            </div>
            <div>
              <dt>Przychody</dt>
              <dd>{formatMoney(lookup.stats.finance_income)}</dd>
            </div>
            <div>
              <dt>Wydatki</dt>
              <dd>{formatMoney(lookup.stats.finance_expense)}</dd>
            </div>
            <div>
              <dt>Bilans</dt>
              <dd>{formatMoney(lookup.stats.finance_balance)}</dd>
            </div>
            <div>
              <dt>Transakcje</dt>
              <dd>{lookup.stats.finance_transaction_count}</dd>
            </div>
          </dl>
        ) : null}
        <div className="modal__actions">
          <button type="button" className="btn" onClick={cancelAction}>
            Zamknij
          </button>
        </div>
      </FormModal>
    </>
  );
}
